/**
 * PAPPO — typingSuggestions.ts
 * Sugestões de digitação LOCAIS (sem IA, sem chamada de rede) para o compositor
 * de mensagens: uma espécie de "texto preditivo" em PT-BR.
 *
 * `getSuggestions(text)` olha a última palavra parcial (ou o início de uma frase
 * curta), ignorando maiúsculas/minúsculas e acentos, e devolve até 3 mensagens
 * JÁ COMPLETADAS — prontas para substituir o texto atual do campo.
 *
 * O dicionário é propositalmente pequeno e fácil de estender: basta acrescentar
 * uma entrada em WORD_SUGGESTIONS (gatilho de UMA palavra) ou em
 * PHRASE_SUGGESTIONS (gatilho de várias palavras). As chaves ficam SEM acento e
 * em minúsculas; os valores são as frases finais, já com acentuação e pontuação.
 */

/** Máximo de chips mostrados de uma vez. */
const MAX_SUGGESTIONS = 3;

/** Só sugere a partir de 2 caracteres (evita disparar a cada tecla). */
const MIN_PREFIX = 2;

/** minúsculas + remove acentos (para casar "obrigadá" ~ "obrigada", "tá" ~ "ta"). */
function stripAccents(s: string): string {
  return s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
}

/** Normaliza uma PALAVRA: sem acento, só letras/números (tira pontuação nas pontas). */
function normWord(s: string): string {
  return stripAccents(s).replace(/[^a-z0-9]/g, '');
}

/** Normaliza uma FRASE inteira: sem acento, só letras/números e espaços simples. */
function normPhrase(s: string): string {
  return stripAccents(s).replace(/[^a-z0-9\s]/g, '').replace(/\s+/g, ' ').trim();
}

/** No meio da frase, começa a sugestão em minúscula (a menos que venha após . ! ?). */
const SENTENCE_END = /[.!?…]\s*$/;
function adjustCase(out: string, head: string): string {
  if (head.trim() === '' || SENTENCE_END.test(head)) return out;
  return out.charAt(0).toLowerCase() + out.slice(1);
}

/**
 * Gatilhos de UMA palavra → mensagens completas.
 * Chave (sem acento, minúscula) é o começo da palavra que a pessoa digita.
 */
const WORD_SUGGESTIONS: Record<string, string[]> = {
  // Saudações
  bom: ['Bom dia', 'Bom trabalho', 'Bom apetite', 'Bom fim de semana'],
  boa: ['Boa tarde', 'Boa noite', 'Boa sorte'],
  oi: ['Oi!', 'Oi, tudo bem?', 'Oi, tudo bom?'],
  ola: ['Olá!', 'Olá, tudo bem?'],
  eai: ['E aí!', 'E aí, tudo bem?'],
  eae: ['E aí!', 'E aí, beleza?'],
  opa: ['Opa!', 'Opa, tudo certo?'],
  tchau: ['Tchau!', 'Tchau, até logo!', 'Tchau, beijos!'],
  ate: ['Até logo!', 'Até já!', 'Até amanhã!'],

  // Educação / agradecimento
  obg: ['Obrigado!', 'Obrigada!', 'Obrigado por tudo!'],
  obrigad: ['Obrigado!', 'Obrigada!'],
  vlw: ['Valeu!', 'Valeu, viu!'],
  valeu: ['Valeu!'],
  pfv: ['Por favor', 'Por favor 🙏'],
  desculp: ['Desculpa!', 'Desculpa o atraso.', 'Desculpa qualquer coisa.'],
  dsclp: ['Desculpa!'],

  // Respostas rápidas
  blz: ['Beleza!', 'Beleza, combinado!'],
  beleza: ['Beleza!'],
  combinad: ['Combinado!'],
  ta: ['Tá bom!', 'Tá certo!', 'Tá ótimo!'],
  sim: ['Sim!', 'Sim, claro!', 'Sim, pode ser!'],
  nao: ['Não!', 'Não posso agora.', 'Não sei ainda.'],
  ja: ['Já vou!', 'Já chego', 'Já resolvo'],
  chego: ['Chego já!', 'Chego em casa já já'],
  tudo: ['Tudo bem?', 'Tudo certo!', 'Tudo ótimo!'],
  como: ['Como você está?', 'Como foi hoje?'],
  onde: ['Onde você está?', 'Onde a gente se encontra?'],
  que: ['Que legal!', 'Que horas?', 'Que saudade!'],

  // Carinho / família
  te: ['Te amo!', 'Te amo muito!', 'Te espero'],
  amo: ['Amo você!', 'Amo muito vocês!'],
  amor: ['Amor, cadê você?', 'Amor, te amo!'],
  saudad: ['Saudade!', 'Saudade de você!', 'Que saudade!'],
  sdd: ['Saudade!', 'Saudade de vocês!'],
  bjs: ['Beijos!', 'Beijo grande!'],
  abs: ['Abraço!', 'Abraços!'],
  abraco: ['Abraço!', 'Um abraço grande!'],
  parabens: ['Parabéns!', 'Parabéns pra você!'],
  feliz: ['Feliz aniversário!', 'Feliz Natal!', 'Feliz por você!'],

  // Amigável para crianças
  mae: ['Mãe, cadê você?', 'Mãe, te amo!', 'Mãe, posso?'],
  pai: ['Pai, cadê você?', 'Pai, te amo!', 'Pai, posso?'],
  vovo: ['Vovó, te amo!', 'Vovô, te amo!'],
  posso: ['Posso brincar?', 'Posso ir?', 'Posso lanchar?'],
  cade: ['Cadê você?', 'Cadê a mamãe?', 'Cadê o papai?'],
  quero: ['Quero brincar!', 'Quero um abraço!', 'Quero ir também!'],
  amei: ['Amei!', 'Amei muito!'],
  legal: ['Que legal!', 'Muito legal!'],
  oba: ['Oba!', 'Oba, adorei!'],
  cuidado: ['Cuidado!', 'Cuidado no caminho!'],
};

/**
 * Gatilhos de VÁRIAS palavras → mensagens completas.
 * A chave é comparada com o TEXTO inteiro já digitado (normalizado): se a chave
 * começa com o que a pessoa escreveu, a mensagem completa substitui tudo.
 * Útil quando a última palavra sozinha ("bom" em "tá bom") levaria a outra coisa.
 */
const PHRASE_SUGGESTIONS: Record<string, string[]> = {
  'ta bom': ['Tá bom!'],
  'ta bem': ['Tá bem!'],
  'ta certo': ['Tá certo!'],
  'ta otimo': ['Tá ótimo!'],
  'por favor': ['Por favor'],
  'ja vou': ['Já vou!'],
  'ja to indo': ['Já tô indo!'],
  'chego ja': ['Chego já!'],
  'te amo': ['Te amo!', 'Te amo muito!'],
  'tudo bem': ['Tudo bem?', 'Tudo bem com você?'],
  'tudo certo': ['Tudo certo!'],
};

/** Junta preservando ordem e sem repetir. */
function pushUnique(target: string[], items: string[]): void {
  for (const it of items) {
    if (it && !target.includes(it)) target.push(it);
  }
}

/**
 * Devolve até 3 mensagens completas sugeridas para o texto atual.
 * As sugestões substituem o texto do campo (a UI faz `setValue(sugestão)`).
 */
export function getSuggestions(text: string): string[] {
  if (!text) return [];
  // Sem palavra parcial no fim (terminou com espaço) → não sugere.
  const m = text.match(/(\S+)$/);
  if (!m || m.index === undefined) return [];

  const word = m[1];
  const head = text.slice(0, m.index); // tudo antes da última palavra (mantém contexto)
  const wKey = normWord(word);
  const whole = normPhrase(text);

  // 1) Frases: o texto inteiro é o começo de um gatilho de várias palavras.
  const phraseOuts: string[] = [];
  if (whole.length >= MIN_PREFIX) {
    for (const key in PHRASE_SUGGESTIONS) {
      if (key.includes(' ') && key.startsWith(whole)) {
        pushUnique(phraseOuts, PHRASE_SUGGESTIONS[key]);
      }
    }
  }

  // 2) Palavras: a última palavra é o começo de um gatilho de uma palavra.
  const wordOuts: string[] = [];
  if (wKey.length >= MIN_PREFIX) {
    for (const key in WORD_SUGGESTIONS) {
      if (key.startsWith(wKey)) pushUnique(wordOuts, WORD_SUGGESTIONS[key]);
    }
  }

  let results: string[] = [];
  if (head.trim() === '') {
    // Palavra única: frase e palavra já são mensagens completas.
    pushUnique(results, phraseOuts);
    pushUnique(results, wordOuts);
  } else if (phraseOuts.length > 0) {
    // Meio de frase que casa um gatilho de frase → substitui tudo (evita colagens feias).
    pushUnique(results, phraseOuts);
  } else {
    // Meio de frase: completa a última palavra mantendo o contexto anterior.
    pushUnique(results, wordOuts.map((out) => head + adjustCase(out, head)));
  }

  // Não sugere algo idêntico ao que já está escrito (mas MANTÉM versões que só
  // acrescentam acento/pontuação, ex.: "ta bom" → "Tá bom!"). Compara exato.
  const cur = text.trim().toLowerCase();
  results = results.filter((r) => r.trim().toLowerCase() !== cur);

  return results.slice(0, MAX_SUGGESTIONS);
}
